import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, MapPin, Plus, X, Sparkles, Search } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CreateAnnoncePage() {
  const navigate = useNavigate();
  const { isAuthenticated, api } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    offer_category_id: '',
    want_categories: [],
    want_text: '',
    duration_hours: 1,
    location: 'Ottignies-Louvain-la-Neuve',
    open_to_all: false,
    image_url: '',
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchCategories();
  }, [isAuthenticated, navigate]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const toggleWantCategory = (categoryId) => {
    setForm(prev => ({
      ...prev,
      want_categories: prev.want_categories.includes(categoryId)
        ? prev.want_categories.filter(id => id !== categoryId)
        : [...prev.want_categories, categoryId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title || !form.description || !form.offer_category_id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!form.open_to_all && form.want_categories.length === 0) {
      toast.error('Sélectionnez au moins une catégorie recherchee ou cochez "Ouvert à tout"');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/annonces', form);
      toast.success('Annonce créée avec succès !');
      // Redirect to matches page
      navigate(`/annonces/${response.data.id}/matches`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la creation');
    } finally {
      setLoading(false);
    }
  };

  const selectedOfferCategory = categories.find(c => c.id === form.offer_category_id);

  return (
    <Layout>
      <div className="container-app py-8 md:py-12 max-w-3xl">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Créer une annonce</h1>
          <p className="text-muted-foreground">
            Proposez ce que vous savez faire et indiquez ce que vous recherchez en échange
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Section 1: Je propose */}
          <Card className="rounded-2xl border-primary/20">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg text-primary">
                <Plus className="w-5 h-5" />
                Je propose
              </CardTitle>
              <CardDescription>
                Décrivez le service ou la competence que vous offrez
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Titre de votre offre *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex: Cours de guitare pour debutants"
                  required
                  className="rounded-xl"
                  data-testid="annonce-title-input"
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Catégorie *</Label>
                <Select
                  value={form.offer_category_id}
                  onValueChange={(value) => setForm({ ...form, offer_category_id: value })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="offer-category-select">
                    <SelectValue placeholder="Sélectionnez une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Décrivez votre offre en detail..."
                  required
                  className="rounded-xl min-h-[120px]"
                  data-testid="annonce-description-input"
                />
              </div>

              {/* Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duration">Durée proposee (heures)</Label>
                  <Select
                    value={form.duration_hours.toString()}
                    onValueChange={(value) => setForm({ ...form, duration_hours: parseFloat(value) })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.5">30 minutes</SelectItem>
                      <SelectItem value="1">1 heure</SelectItem>
                      <SelectItem value="1.5">1h30</SelectItem>
                      <SelectItem value="2">2 heures</SelectItem>
                      <SelectItem value="3">3 heures</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Localisation *</Label>
                  <Input
                    id="location"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="Ottignies-Louvain-la-Neuve"
                    required
                    className="rounded-xl"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 2: Je cherche */}
          <Card className="rounded-2xl border-secondary/20">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg text-secondary">
                <Search className="w-5 h-5" />
                Je cherche en échange
              </CardTitle>
              <CardDescription>
                Sélectionnez les types de services qui vous interessent
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Open to all checkbox */}
              <div className="flex items-center space-x-2 p-3 rounded-xl bg-muted/50">
                <Checkbox
                  id="open_to_all"
                  checked={form.open_to_all}
                  onCheckedChange={(checked) => setForm({ ...form, open_to_all: checked, want_categories: checked ? [] : form.want_categories })}
                />
                <label
                  htmlFor="open_to_all"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Ouvert à tout type d&apos;échange
                </label>
              </div>

              {/* Category selection */}
              {!form.open_to_all && (
                <div className="space-y-3">
                  <Label>Sélectionnez les categories qui vous interessent *</Label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <Badge
                        key={cat.id}
                        variant={form.want_categories.includes(cat.id) ? "default" : "outline"}
                        className={`cursor-pointer transition-all py-2 px-3 ${
                          form.want_categories.includes(cat.id)
                            ? 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                            : 'hover:bg-muted'
                        }`}
                        onClick={() => toggleWantCategory(cat.id)}
                        data-testid={`want-category-${cat.slug}`}
                      >
                        {cat.icon} {cat.name}
                        {form.want_categories.includes(cat.id) && (
                          <X className="w-3 h-3 ml-1" />
                        )}
                      </Badge>
                    ))}
                  </div>
                  {form.want_categories.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      {form.want_categories.length} categorie(s) selectionnee(s)
                    </p>
                  )}
                </div>
              )}

              {/* Want text (optional details) */}
              <div className="space-y-2">
                <Label htmlFor="want_text">Details supplementaires (optionnel)</Label>
                <Textarea
                  id="want_text"
                  value={form.want_text}
                  onChange={(e) => setForm({ ...form, want_text: e.target.value })}
                  placeholder="Précisez ce que vous recherchez..."
                  className="rounded-xl min-h-[80px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          {form.title && form.offer_category_id && (
            <Card className="rounded-2xl bg-muted/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Apercu de votre annonce</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="font-medium">{form.title}</p>
                  <div className="flex flex-wrap gap-2 text-sm">
                    <Badge variant="default" className="bg-primary">
                      {selectedOfferCategory?.icon} Je propose: {selectedOfferCategory?.name}
                    </Badge>
                    {form.open_to_all ? (
                      <Badge variant="secondary">Ouvert à tout</Badge>
                    ) : form.want_categories.length > 0 && (
                      <Badge variant="secondary">
                        Je cherche: {form.want_categories.length} categorie(s)
                      </Badge>
                    )}
                    <Badge variant="outline">{form.duration_hours}h</Badge>
                    <Badge variant="outline">{form.location}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submit */}
          <Button
            type="submit"
            className="w-full btn-primary h-12 text-lg"
            disabled={loading}
            data-testid="submit-annonce-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Creation en cours...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Publier et voir mes matchs
              </>
            )}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
